import React, { useState, useEffect } from "react"
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Alert,
    Paper,
    Typography,
    Button,
} from "@mui/material"
import ClearIcon from "@mui/icons-material/Clear"
import { useTranslation } from "react-i18next"
import apiClient from "../../services/apiClient"
import { useCompanyId } from "../../hooks/useCompanyId"

export interface SharedFilterValues {
    outletId: string
    registerId: string
    dateFrom: string
    dateTo: string
}

interface SharedFiltersProps {
    filters: SharedFilterValues
    onFiltersChange: (filters: SharedFilterValues) => void
    onDateError?: (error: string | null) => void
}

function todayISO(): string {
    return new Date().toISOString().split("T")[0]
}

const SharedFilters: React.FC<SharedFiltersProps> = ({
    filters,
    onFiltersChange,
    onDateError,
}) => {
    const { t } = useTranslation()
    const { companyId } = useCompanyId()
    const [outlets, setOutlets] = useState<any[]>([])
    const [registers, setRegisters] = useState<any[]>([])
    const [dateError, setDateError] = useState<string | null>(null)

    // Load outlets
    useEffect(() => {
        if (!companyId) {
            setOutlets([])
            return
        }
        let cancelled = false
        ;(async () => {
            try {
                const res = await apiClient.get(
                    `/api/outlets?company_id=${companyId}`
                )
                if (!cancelled) setOutlets(res.outlets || [])
            } catch {
                if (!cancelled) setOutlets([])
            }
        })()
        return () => {
            cancelled = true
        }
    }, [companyId])

    // Load registers when outlet changes
    useEffect(() => {
        if (!companyId) {
            setRegisters([])
            return
        }
        let cancelled = false
        ;(async () => {
            try {
                const params = filters.outletId
                    ? `company_id=${companyId}&outlet_id=${filters.outletId}`
                    : `company_id=${companyId}`
                const res = await apiClient.get(`/api/registers?${params}`)
                if (!cancelled) setRegisters(res.registers || [])
            } catch {
                if (!cancelled) setRegisters([])
            }
        })()
        return () => {
            cancelled = true
        }
    }, [companyId, filters.outletId])

    // Validate date range
    useEffect(() => {
        if (filters.dateFrom && filters.dateTo) {
            const from = new Date(filters.dateFrom)
            const to = new Date(filters.dateTo)
            const diffDays =
                (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)
            if (diffDays > 365) {
                const error = String(
                    t("exportsComponents.sharedFilters.errors.maxRange")
                )
                setDateError(error)
                onDateError?.(error)
            } else if (diffDays < 0) {
                const error = String(
                    t("exportsComponents.sharedFilters.errors.endBeforeStart")
                )
                setDateError(error)
                onDateError?.(error)
            } else {
                setDateError(null)
                onDateError?.(null)
            }
        } else {
            setDateError(null)
            onDateError?.(null)
        }
    }, [filters.dateFrom, filters.dateTo, onDateError])

    const update = (key: keyof SharedFilterValues, value: string) => {
        const next = { ...filters, [key]: value }
        // Reset register when outlet changes
        if (key === "outletId") {
            next.registerId = ""
        }
        onFiltersChange(next)
    }

    const handleClear = () => {
        onFiltersChange({
            outletId: "",
            registerId: "",
            dateFrom: todayISO(),
            dateTo: todayISO(),
        })
    }

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1.5,
                }}
            >
                <Typography variant="subtitle2" color="text.secondary">
                    {t("exportsComponents.sharedFilters.title")}
                </Typography>
                <Button
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={handleClear}
                >
                    {t("exportsComponents.sharedFilters.reset")}
                </Button>
            </Box>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        md: "1fr 1fr 1fr 1fr",
                    },
                    gap: 2,
                }}
            >
                <FormControl fullWidth size="small">
                    <InputLabel shrink>
                        {t("exportsComponents.sharedFilters.outlet")}
                    </InputLabel>
                    <Select
                        value={filters.outletId}
                        label={String(
                            t("exportsComponents.sharedFilters.outlet")
                        )}
                        displayEmpty
                        renderValue={(value) => {
                            const selectedId = String(value || "")
                            if (!selectedId)
                                return t("exportsComponents.sharedFilters.all")
                            const selectedOutlet = outlets.find(
                                (o) => o.id === selectedId
                            )
                            return (
                                selectedOutlet?.name ||
                                t("exportsComponents.sharedFilters.all")
                            )
                        }}
                        onChange={(e) => update("outletId", e.target.value)}
                    >
                        <MenuItem value="">
                            {t("exportsComponents.sharedFilters.all")}
                        </MenuItem>
                        {outlets.map((o) => (
                            <MenuItem key={o.id} value={o.id}>
                                {o.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                    <InputLabel shrink>
                        {t("exportsComponents.sharedFilters.register")}
                    </InputLabel>
                    <Select
                        value={filters.registerId}
                        label={String(
                            t("exportsComponents.sharedFilters.register")
                        )}
                        displayEmpty
                        renderValue={(value) => {
                            const selectedId = String(value || "")
                            if (!selectedId)
                                return t("exportsComponents.sharedFilters.all")
                            const selectedRegister = registers.find(
                                (r) => r.id === selectedId
                            )
                            if (!selectedRegister)
                                return t("exportsComponents.sharedFilters.all")
                            return `${selectedRegister.name}${
                                selectedRegister.outlet_name
                                    ? ` (${selectedRegister.outlet_name})`
                                    : ""
                            }`
                        }}
                        onChange={(e) => update("registerId", e.target.value)}
                    >
                        <MenuItem value="">
                            {t("exportsComponents.sharedFilters.all")}
                        </MenuItem>
                        {registers.map((r) => (
                            <MenuItem key={r.id} value={r.id}>
                                {r.name}
                                {r.outlet_name ? ` (${r.outlet_name})` : ""}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label={String(
                        t("exportsComponents.sharedFilters.dateFrom")
                    )}
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => update("dateFrom", e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                />

                <TextField
                    label={String(t("exportsComponents.sharedFilters.dateTo"))}
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => update("dateTo", e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                />
            </Box>
            {dateError && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                    {dateError}
                </Alert>
            )}
        </Paper>
    )
}

export { todayISO }
export default SharedFilters
